import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { IotaModalSize, MatDialogRef } from '@iota/iota-ui-components';
import { companies, companiesWithRoleCustomer } from 'src/app/store/reducer/company.reducer';
import {
    payrollReportCreationProcessCurrentStep,
    payrollReportCreationProcessDataValid,
    payrollReportCreationProcessError,
    payrollReportCreationProcessIsInResultStep,
    payrollReportCreationProcessIsInSettingsStep,
    payrollReportCreationProcessLatestResults,
    payrollReportCreationProcessLoading,
    payrollReportCreationProcessSelectedAsOfDate,
    payrollReportCreationProcessSelectedCompanies
} from 'src/app/store/reducer/payroll-report-creation-process.reducer';
import { PayrollReportCreationProcessStepLabels } from '../../constants-and-enums/payroll-report-creation-process-types';
import { combineLatest, map } from 'rxjs';
import { Store } from '@ngrx/store';
import {
    InitPayrollReportCreationForm,
    PayrollReportCreationProcessUpsertForm,
    StartPayrollReportCreationProcess
} from 'src/app/store/actions/payroll-report-creation-process.actions';
import { MonthAndYearPickerComponent } from '../../components/month-and-year-picker/month-and-year-picker.component';
import {
    sortByNameWithAlphabeticCharactersFirst,
    getCompanyName,
    getFirstDeactivatedCompanyIndex,
    isCompanyDeactivated,
    sortCompaniesByDeactivationStatus,
    toApiDateString
} from '../../utils';

@Component({
    standalone: false,
    selector: 'app-start-payroll-report-process-dialog',
    templateUrl: './start-payroll-report-process-dialog.component.html',
    styleUrls: ['./start-payroll-report-process-dialog.component.scss']
})
export class StartPayrollReportProcessDialogComponent implements OnInit, AfterViewInit {
    @ViewChild(MonthAndYearPickerComponent) monthAndYearPicker: MonthAndYearPickerComponent;

    public modalOptions = {
        size: IotaModalSize.SIZE_3XL
    };

    companies$ = this.store.select(companiesWithRoleCustomer).pipe(
        map((companies) => sortByNameWithAlphabeticCharactersFirst(companies)),
        map((companies) => sortCompaniesByDeactivationStatus(companies))
    );

    isInSettingsStep$ = this.store.select(payrollReportCreationProcessIsInSettingsStep);
    isInResultStep$ = this.store.select(payrollReportCreationProcessIsInResultStep);

    stepLabels = PayrollReportCreationProcessStepLabels;
    currentStep$ = this.store.select(payrollReportCreationProcessCurrentStep);

    loading$ = this.store.select(payrollReportCreationProcessLoading);
    latestResults$ = this.store.select(payrollReportCreationProcessLatestResults);
    error$ = this.store.select(payrollReportCreationProcessError);

    selectedCompanies$ = this.store.select(payrollReportCreationProcessSelectedCompanies);
    selectedAsOfDate$ = this.store.select(payrollReportCreationProcessSelectedAsOfDate);

    companiesSelectLabel$ = combineLatest([
        this.store.select(companies),
        this.store.select(payrollReportCreationProcessSelectedCompanies)
    ]).pipe(
        map(([allCompanies, selectedCompanies]) => {
            return this.getCompanySelectLabel(allCompanies?.filter((company) => selectedCompanies.includes(company.companyId)));
        })
    );

    getCompanySelectLabel(selectedBusinessPartners: { name?: string }[]): string {
        if (!selectedBusinessPartners || selectedBusinessPartners.length === 0) return '';
        if (selectedBusinessPartners.length === 1) return selectedBusinessPartners[0]?.name;

        return selectedBusinessPartners[0]?.name + ` (+${selectedBusinessPartners.length - 1})`;
    }

    dataValid$ = this.store.select(payrollReportCreationProcessDataValid);

    constructor(
        private dialogRef: MatDialogRef<StartPayrollReportProcessDialogComponent>,
        private store: Store
    ) {}

    ngOnInit(): void {
        this.store.dispatch(InitPayrollReportCreationForm());
    }

    ngAfterViewInit(): void {
        // We need make sure the element is available and set the initial value
        setTimeout(() => {
            this.updateAsOfDate();
        });
    }

    updateSelectedCompanies(event) {
        this.store.dispatch(PayrollReportCreationProcessUpsertForm({ data: { companyIds: event.value } }));
    }

    clearSelectedCompanies() {
        this.store.dispatch(PayrollReportCreationProcessUpsertForm({ data: { companyIds: [] } }));
    }

    updateAsOfDate() {
        const asOfDate = toApiDateString(this.monthAndYearPicker?.getWithDayOfMonth(15));
        this.store.dispatch(PayrollReportCreationProcessUpsertForm({ data: { asOfDate } }));
    }

    startPayrollReportProcess() {
        this.store.dispatch(StartPayrollReportCreationProcess());
    }

    getCompanyName = getCompanyName;
    getFirstDeactivatedIndex = getFirstDeactivatedCompanyIndex;
    isCompanyDeactivated = isCompanyDeactivated;

    closeDialog() {
        this.dialogRef.close();
    }
}
